import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';

import { DocumentLayoutComponent, DocumentPdfSideBySideComponent, DocumentPdfSideBySideStore } from '@lib/document';
import { InvoiceDetailComponent } from '../invoice-detail.component';
import { BaseSiteLayoutModule, MobileModule, MobileService } from '@lib/util';
import { TranslateModule } from '@ngx-translate/core';
import { InvoiceDetailDisplayStore } from './invoice-detail-display.store';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IFileResponse, SseDataType, SseService, View, PdfPreviewComponent, isPdf } from '@lib/core';
import { InvoicePossibleActionType } from '../types/InvoicePossibleActionType';
import { InvoiceStatus } from '@lib/invoice-public';
import { InvoiceDetail } from '../types/InvoiceDetail';
import { debounceTime, filter, Subscription } from 'rxjs';

@Component({
    selector: 'invoice-detail-display',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        InvoiceDetailComponent,
        BaseSiteLayoutModule,
        TranslateModule,
        MobileModule,
        PdfPreviewComponent,
        DocumentLayoutComponent,
        DocumentPdfSideBySideComponent,
        RouterLink,
    ],
    templateUrl: './invoice-detail-display.component.html',
    styleUrl: './invoice-detail-display.component.scss',
    providers: [InvoiceDetailDisplayStore, DocumentPdfSideBySideStore],
})
export class InvoiceDetailDisplayComponent implements OnInit {
    public readonly store = inject(InvoiceDetailDisplayStore);
    public readonly router = inject(Router);
    public readonly pdfSideBySide = inject(DocumentPdfSideBySideStore);

    public readonly invoice: Signal<InvoiceDetail>;
    public readonly editAllowed: Signal<boolean>;
    public readonly markAsPaidAllowed: Signal<boolean>;
    public readonly invoiceFiles: Signal<IFileResponse[]>;
    public readonly hasPdfFileAttachments: Signal<boolean>;
    public readonly showPdfPreview: Signal<boolean>;
    public readonly loadError: Signal<number | undefined>;
    public readonly hasLoadError: Signal<boolean>;
    public readonly isLoading: Signal<boolean>;

    private readonly _destroyRef = inject(DestroyRef);
    private readonly _route = inject(ActivatedRoute);
    private readonly _sseService = inject(SseService);
    private readonly _mobileService = inject(MobileService);
    private _statusUpdateSub: Subscription;

    private readonly _pdfPreviewActive: WritableSignal<boolean> = signal(true);

    constructor() {
        this.invoice = this.store.invoice;
        this.loadError = computed(() => this.store.loadError());
        this.hasLoadError = computed(() => this.loadError() !== undefined);
        this.isLoading = computed(() => this.store.loading());
        this.editAllowed = computed(() => {
            return this.invoice().possibleActions.includes(InvoicePossibleActionType.EDIT);
        });
        this.markAsPaidAllowed = computed(() => {
            return this.invoice().possibleActions.includes(InvoicePossibleActionType.MARK_AS_PAID);
        });

        this.invoiceFiles = computed(() => {
            const inv = this.invoice();
            return inv?.files ?? [];
        });

        this.hasPdfFileAttachments = computed(() => {
            const inv = this.invoice();
            if (!inv) {
                return false;
            }
            const files = inv.files;
            const isMobile = this._mobileService.isMobile().mobile;
            return files.some((file) => isPdf(file.fileType)) && !isMobile;
        });

        this.showPdfPreview = computed(() => {
            const inv = this.invoice();
            if (!inv) {
                return false;
            }
            return (this.hasPdfFileAttachments() && this._pdfPreviewActive()) || this.pdfSideBySide.isOpen();
        });
    }

    togglePdfPreview(): void {
        const hide = this.showPdfPreview();
        this.pdfSideBySide.close();
        this._pdfPreviewActive.set(!hide);
    }

    ngOnInit(): void {
        this.listenToRouteChanges();
    }

    filesChanged(files: IFileResponse[]): void {
        this.store.filesChanged(files);
    }

    onMarkInvoiceAsPaid() {
        this.store.markInvoicePaid();
    }

    onEditInvoice(): void {
        this._route.params.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params: Params) => {
            const id = params.id;
            this.router.navigate([View.CLIENT, 'my-admin', 'invoice', 'create', 'standalone'], {
                queryParams: {
                    invoiceId: id,
                },
            });
        });
    }

    private listenToRouteChanges(): void {
        this._route.params.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params: Params) => {
            const id = params.id;
            if (id) {
                this.listenToStatusChanges(id);
                this.store.loadData(id);
            }
        });
    }

    protected readonly InvoiceStatus = InvoiceStatus;
    protected readonly InvoicePossibleActionType = InvoicePossibleActionType;

    private listenToStatusChanges(id: string) {
        if (this._statusUpdateSub) {
            this._statusUpdateSub?.unsubscribe();
        }
        this._statusUpdateSub = this._sseService
            .getEventsForType<{
                invoiceId: string;
            }>(SseDataType.InvoiceStatusChangeNotification)
            .pipe(
                filter((event: { invoiceId: string }) => event.invoiceId === id),
                debounceTime(1000),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe(() => {
                this.store.loadData(id);
            });
    }
}
